import { useState } from 'react'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, Text } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { settlementsService } from '../../api/services/settlements.service'
import { usePagedRequest } from '../../hooks'
import type {
  PendingSettlementListItemResponse,
  SettlementListItemResponse,
} from '../../api/models/response'
import { useT } from '../../i18n'
import { parseUtcDate } from '../../lib/dates'

const formatWeight = (v: number | null) =>
  v == null
    ? '-'
    : `${v.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} t`

const formatNumber = (v: number | null | undefined, digits = 2) =>
  v == null ? '-' : v.toLocaleString('es-PE', { minimumFractionDigits: digits, maximumFractionDigits: digits })

const formatUsd = (v: number | null | undefined) =>
  v == null
    ? '-'
    : `$ ${v.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '-'
  return parseUtcDate(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

type TabKey = 'pending' | 'liquidated'

export default function LiquidacionesPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<TabKey>('pending')

  // Pendientes (lotes ya pesados, listos para liquidar)
  const pending = usePagedRequest(settlementsService.getPending, {
    params: { search: search.trim() || undefined },
  })

  // Ya liquidados
  const liquidated = usePagedRequest(settlementsService.getAll, {
    params: { search: search.trim() || undefined },
  })

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title={t('liquidaciones.title')}
        subtitle="Lotes aceptados en Conciliación, listos para liquidar"
        colors={colors}
      />

      {/* Tabs */}
      <Flex gap={2}>
        <Box
          as="button"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="600"
          cursor="pointer"
          bg={tab === 'pending' ? brand.primary : '#efe8dd'}
          color={tab === 'pending' ? 'white' : '#4f4842'}
          onClick={() => setTab('pending')}
        >
          Por liquidar
        </Box>
        <Box
          as="button"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="600"
          cursor="pointer"
          bg={tab === 'liquidated' ? brand.primary : '#efe8dd'}
          color={tab === 'liquidated' ? 'white' : '#4f4842'}
          onClick={() => setTab('liquidated')}
        >
          Liquidados
        </Box>
      </Flex>

      {tab === 'pending' ? (
        <DataList<PendingSettlementListItemResponse>
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('liquidaciones.searchPlaceholder')}
          page={pending.page}
          totalPages={pending.totalPages}
          onPageChange={pending.setPage}
          totalCount={pending.totalCount}
          entityName="lotes"
          pageSize={pending.pageSize}
          onPageSizeChange={pending.setPageSize}
          brandColor={brand.green}
          headers={[
            'Lote',
            'Proveedor',
            'Mineral',
            'Peso Bruto',
            'Peso Neto',
            'Ley',
            'Humedad',
          ]}
          data={pending.items}
          loading={pending.isFetching}
          error={pending.error}
          emptyMessage="No hay lotes pendientes de liquidacion"
          loadingMessage={t('liquidaciones.loadingMessage')}
          colors={colors}
          keyExtractor={(s) => s.id}
          renderRow={(s) => (
            <>
              <DataTable.Cell>
                <Text
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.text}
                >
                  {s.batchCode}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                    {s.supplierName || '-'}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                    {s.supplierRuc ?? ''}
                  </Text>
                </VStack>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="500" color={brand.green}>
                  {s.mineralTypeName || '-'}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatWeight(s.grossWeight)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="600" color={colors.text}>
                  {formatWeight(s.netWeight)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(s.mineralGrade, 4)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(s.moisture, 2)}
                </Text>
              </DataTable.Cell>
            </>
          )}
          actions={(row) => [
            {
              label: 'Liquidar',
              onClick: () => navigate(`/app/cierre/liquidaciones/${row.id}/liquidar`),
            },
            {
              label: 'Ver Detalle',
              onClick: () => navigate(`/app/cierre/liquidaciones/${row.id}/liquidar?readOnly=1`),
            },
          ]}
        />
      ) : (
        <DataList<SettlementListItemResponse>
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar por lote o proveedor..."
          page={liquidated.page}
          totalPages={liquidated.totalPages}
          onPageChange={liquidated.setPage}
          totalCount={liquidated.totalCount}
          entityName="liquidaciones"
          pageSize={liquidated.pageSize}
          onPageSizeChange={liquidated.setPageSize}
          brandColor={brand.green}
          headers={[
            'Lote',
            'Proveedor',
            'Mineral',
            'Peso Bruto',
            'Peso Neto',
            'Humedad',
            'Fecha Aprobación',
            'Fecha Liquidación',
            'Importe (USD)',
          ]}
          data={liquidated.items}
          loading={liquidated.isFetching}
          error={liquidated.error}
          emptyMessage="No hay liquidaciones registradas"
          loadingMessage={t('liquidaciones.loadingMessage')}
          colors={colors}
          keyExtractor={(s) => s.id}
          renderRow={(s) => (
            <>
              <DataTable.Cell>
                <Text
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.text}
                >
                  {s.batchCode}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                    {s.supplierName || '-'}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                    {s.supplierRuc ?? ''}
                  </Text>
                </VStack>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="500" color={brand.green}>
                  {s.mineralTypeName || '-'}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatWeight(s.grossWeight)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="600" color={colors.text}>
                  {formatWeight(s.plantWeight)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" color={colors.text}>
                  {formatNumber(s.moisture, 2)}%
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                  {formatDate(s.approvedAt)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                  {formatDate(s.settlementDate)}
                </Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="700" color={brand.primary}>
                  {formatUsd(s.purchasePrice)}
                </Text>
              </DataTable.Cell>
            </>
          )}
          actions={(row) => [
            {
              label: 'Ver Detalle',
              onClick: () =>
                navigate(`/app/cierre/liquidaciones/${row.shipmentRecordId}/liquidar?readOnly=1`),
            },
          ]}
        />
      )}
    </VStack>
  )
}
